import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Clock, Loader2, Video } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TimerButton } from "@/components/TimerButton";
import { TodoItem } from "@/components/TodoItem";
import { cn } from "@/lib/utils";
import { OnboardingState, Step, SubStep } from "@/lib/types";
import { useIsMobile } from "@/hooks/use-mobile";

interface StepContentProps {
  currentStep: Step;
  currentSubStep?: SubStep;
  state: OnboardingState;
  inputValues: Record<string, string>;
  inputErrors: Record<string, string>;
  canProceed: () => boolean;
  isProcessing: boolean;
  handleInputChange: (name: string, value: string, type: string) => void;
  handleNextStep: () => void;
  isTodoCompleted: (stepId: string, todoId: string) => boolean;
}

export function StepContent({
  currentStep,
  currentSubStep,
  state,
  inputValues,
  inputErrors,
  canProceed,
  isProcessing,
  handleInputChange,
  handleNextStep,
  isTodoCompleted,
}: StepContentProps) {
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const mobileInfo = useIsMobile();

  // Focus first input field when component mounts or step/subStep changes
  useEffect(() => {
    const firstInputName = currentSubStep?.inputs?.[0]?.name || currentStep?.inputs?.[0]?.name;
    
    if (firstInputName) {
      setTimeout(() => {
        const inputElement = inputRefs.current[firstInputName];
        if (inputElement) {
          inputElement.focus();
        }
      }, 100);
    }
  }, [currentStep?.id, currentSubStep?.id, state.currentStepIndex, state.currentSubStepIndex, state.currentDayIndex]);

  const isAccountCreatedTodoCompleted = 
    currentStep?.id === 'create-tiktok' && 
    currentSubStep?.id === 'create' && 
    isTodoCompleted(currentStep.id, 'account-created');

  // Helper function to determine appropriate input type and mode
  const getInputTypeProps = (inputType: string) => {
    switch (inputType) {
      case 'email':
        return { type: 'email', inputMode: 'email' as const };
      case 'phone':
        return { type: 'tel', inputMode: 'tel' as const };
      case 'number':
        return { type: 'text', inputMode: 'numeric' as const };
      default:
        return { type: 'text' };
    }
  };

  return (
    <motion.div
      key={`step-${currentStep?.id}-${state.currentSubStepIndex}-${state.currentDayIndex}`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
      className="space-y-5"
    >
      {currentStep?.type === 'dayStep' && currentStep.days && (
        <div className="mb-1">
          <span className="text-sm font-medium px-3 py-1 bg-slate-100 text-slate-600 rounded-full">
            {currentStep.days[state.currentDayIndex]?.title}
          </span>
        </div>
      )}
      
      <h1 className="text-xl md:text-2xl font-bold">
        {currentSubStep?.title || currentStep?.title}
      </h1>
      
      {currentStep?.type === 'dayStep' && currentSubStep && (
        <div className="flex items-center">
          <Clock className="h-4 w-4 text-slate-500 mr-1" />
          <span className="text-sm text-slate-500">
            Day {state.currentDayIndex + 1} / {currentStep.days?.length}
          </span>
        </div>
      )}
      
      {(currentSubStep?.description || currentStep?.description) && (
        <div className="prose prose-sm max-w-none">
          <p className="whitespace-pre-line text-slate-700 text-sm" 
             dangerouslySetInnerHTML={{ __html: (currentSubStep?.description || currentStep?.description).replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }}>
          </p>
        </div>
      )}
      
      {(currentSubStep?.todos || currentStep?.todos) && (
        <div className="space-y-2 my-4">
          {(currentSubStep?.todos || currentStep?.todos)?.map((todo) => (
            <TodoItem
              key={todo.id}
              todo={todo}
              stepId={currentStep.id}
            />
          ))}
        </div>
      )}
      
      {/* TikTok Username Input - Only show when account-created todo is completed */}
      {isAccountCreatedTodoCompleted && currentStep?.id === 'create-tiktok' && currentSubStep?.id === 'create' && (
        <div className="space-y-4 my-6">
          <AnimatePresence>
            {currentSubStep.inputs && currentSubStep.inputs.map((input, index) => {
              const inputTypeProps = getInputTypeProps(input.type);
              
              return (
                <motion.div 
                  key={input.name} 
                  className="space-y-2"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <label
                    htmlFor={input.name}
                    className="block text-sm font-medium text-slate-700"
                  >
                    {input.label}
                  </label>
                  <Input
                    id={input.name}
                    {...inputTypeProps}
                    placeholder={input.placeholder}
                    value={inputValues[input.name] || ''}
                    onChange={(e) => 
                      handleInputChange(input.name, e.target.value, input.type)
                    }
                    onEnter={() => {
                      if (canProceed() && !isProcessing) {
                        handleNextStep();
                      }
                    }}
                    className={cn(
                      "transition-all duration-200 focus:ring-2 focus:ring-primary/20",
                      inputErrors[input.name] ? 'border-red-300' : '',
                      mobileInfo.isMobile ? "text-base py-3" : ""
                    )}
                    ref={(el) => inputRefs.current[input.name] = el}
                  />
                  {inputErrors[input.name] && (
                    <p className="text-xs text-red-500">{inputErrors[input.name]}</p>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}
      
      {/* Regular inputs for all other steps */}
      {((currentSubStep?.inputs || currentStep?.inputs) && currentStep?.id !== 'create-tiktok') && (
        <div className="space-y-4 my-6">
          {(currentSubStep?.inputs || currentStep?.inputs)?.map((input, index) => {
            const inputTypeProps = getInputTypeProps(input.type);
            
            return (
              <motion.div 
                key={input.name} 
                className="space-y-2"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <label
                  htmlFor={input.name}
                  className="block text-sm font-medium text-slate-700"
                >
                  {input.label}
                </label>
                <Input
                  id={input.name}
                  {...inputTypeProps}
                  placeholder={input.placeholder}
                  value={inputValues[input.name] || ''}
                  onChange={(e) => 
                    handleInputChange(input.name, e.target.value, input.type)
                  }
                  onEnter={() => {
                    if (canProceed() && !isProcessing) {
                      handleNextStep();
                    }
                  }}
                  className={cn(
                    "transition-all duration-200 focus:ring-2 focus:ring-primary/20",
                    inputErrors[input.name] ? 'border-red-300' : '',
                    mobileInfo.isMobile ? "text-base py-3" : ""
                  )}
                  ref={(el) => inputRefs.current[input.name] = el}
                />
                {inputErrors[input.name] && (
                  <p className="text-xs text-red-500">{inputErrors[input.name]}</p>
                )}
              </motion.div>
            );
          })}
        </div>
      )}
      
      {currentStep?.conditionalInput && (
        <AnimatePresence>
          {currentStep.conditionalInput && isTodoCompleted(currentStep.id, currentStep.conditionalInput.todoId) && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ 
                opacity: 1, 
                height: 'auto',
                transition: { duration: 0.3 }
              }}
              exit={{ opacity: 0, height: 0 }}
              className="space-y-2 overflow-hidden p-4 bg-slate-50 rounded-lg border border-slate-100 my-4"
            >
              <label
                htmlFor={currentStep.conditionalInput.input.name}
                className="block text-sm font-medium text-slate-700"
              >
                {currentStep.conditionalInput.input.label}
              </label>
              <Input
                id={currentStep.conditionalInput.input.name}
                type="text"
                placeholder={currentStep.conditionalInput.input.placeholder}
                value={inputValues[currentStep.conditionalInput.input.name] || ''}
                onChange={(e) => 
                  handleInputChange(
                    currentStep.conditionalInput!.input.name, 
                    e.target.value, 
                    'text'
                  )
                }
                onEnter={() => {
                  if (canProceed() && !isProcessing) {
                    handleNextStep();
                  }
                }}
                className="transition-all duration-200 focus:ring-2 focus:ring-primary/20"
                ref={(el) => inputRefs.current[currentStep.conditionalInput.input.name] = el}
              />
            </motion.div>
          )}
        </AnimatePresence>
      )}
      
      {currentStep?.videos && currentStep.videos.length > 0 && (
        <div className="space-y-4 my-6 bg-slate-50 rounded-lg p-4">
          <h3 className="font-medium text-sm">Tutorial Videos:</h3>
          <div className="space-y-3">
            {currentStep.videos.map((video, idx) => (
              <div key={idx} className="space-y-2">
                <h4 className="text-sm font-medium">{video.title}</h4>
                <div className="bg-black/5 rounded flex items-center justify-center p-8 border border-slate-200">
                  <div className="flex items-center gap-2">
                    <Video className="h-6 w-6 text-slate-400" />
                    <span className="text-sm text-slate-500">{video.src}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      <div className="pt-6">
        {currentSubStep?.buttonTimer || currentStep?.buttonTimer ? (
          <TimerButton
            onClick={handleNextStep}
            timerSeconds={currentSubStep?.buttonTimer || currentStep.buttonTimer}
            disabled={!canProceed() || isProcessing}
            className={cn(
              "w-full group",
              mobileInfo.isMobile ? "py-4 text-base" : ""
            )}
          >
            <span className="flex items-center gap-1.5">
              {isProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  {currentSubStep?.buttonText || currentStep.buttonText}
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                </>
              )}
            </span>
          </TimerButton>
        ) : (
          <Button
            onClick={handleNextStep}
            disabled={!canProceed() || isProcessing}
            className={cn(
              "w-full group",
              mobileInfo.isMobile ? "py-6 text-base" : ""
            )}
            size={mobileInfo.isMobile ? "lg" : "default"}
          >
            <span className="flex items-center gap-1.5">
              {isProcessing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  {currentSubStep?.buttonText || currentStep.buttonText}
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                </>
              )}
            </span>
          </Button>
        )}
      </div>
    </motion.div>
  );
}
